import React, { useState, useEffect } from 'react';
import { Upload, Image as ImageIcon, Trash2, Edit, Download, Lock, LogIn } from 'lucide-react';
import AdminUploadPanel from '../components/ui/AdminUploadPanel';
import LogoTransformPanel from '../components/ui/LogoTransformPanel';
import AuthModal from '../components/ui/AuthModal';
import { useAuth } from '../context/AuthContext';

const AdminPage = () => {
  const { isAuthenticated, isAdmin, user, loading: authLoading, getAuthHeaders } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [showTransformPanel, setShowTransformPanel] = useState(false);
  const [filter, setFilter] = useState({
    category: '',
    search: ''
  });

  useEffect(() => {
    if (!authLoading && isAuthenticated && isAdmin) {
      fetchLogos();
    }
  }, [filter, authLoading, isAuthenticated, isAdmin]);

  const fetchLogos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.category) params.append('category', filter.category);
      if (filter.search) params.append('search', filter.search);

      const response = await fetch(`/api/admin/logos?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLogos(data.logos || []);
      } else {
        console.error('Failed to fetch logos');
        setLogos([]);
      }
    } catch (error) {
      console.error('Error fetching logos:', error);
      setLogos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (logo) => {
    setLogos(prev => [logo, ...prev]);
    setShowUploadPanel(false);
  };

  const handleDelete = async (logoId) => {
    if (!confirm('Are you sure you want to delete this logo?')) return;

    try {
      const response = await fetch(`/api/admin/logos/${logoId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setLogos(prev => prev.filter(l => l._id !== logoId));
        alert('Logo deleted successfully');
      } else {
        alert('Failed to delete logo');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting logo');
    }
  };

  const handleTransform = (logo) => {
    setSelectedLogo(logo);
    setShowTransformPanel(true);
  };

  const categories = [
    'All',
    'Institute',
    'Technical Clubs',
    'Cultural Clubs',
    'Social Clubs',
    'Departments',
    'Special'
  ];

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth required if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <AuthModal
          isOpen={!isAuthenticated}
          onClose={() => window.location.href = '/'}
          defaultMode="signin"
        />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
              <Lock className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Admin Access Required</h1>
            <p className="text-gray-600 mb-8">
              You need to sign in with admin credentials to access this page.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full py-3 px-6 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <LogIn className="w-5 h-5" />
              Sign In as Admin
            </button>
          </div>
        </div>
      </>
    );
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
            <Lock className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You are signed in as <strong>{user?.email}</strong>, but this account does not have admin privileges.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Contact the system administrator if you believe you should have access.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full py-3 px-6 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="signin"
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Logo Admin Panel</h1>
              <p className="text-gray-600 mt-1">Manage logos with Cloudinary</p>
            </div>
            <button
              onClick={() => setShowUploadPanel(true)}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Upload className="w-5 h-5 mr-2" />
              Upload New Logo
            </button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search logos..."
                value={filter.search}
                onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={filter.category}
                onChange={(e) => setFilter(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat === 'All' ? '' : cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Logo Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : logos.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No logos found</h3>
            <p className="text-gray-600 mb-6">Upload your first logo to get started</p>
            <button
              onClick={() => setShowUploadPanel(true)}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Upload className="w-5 h-5 mr-2" />
              Upload Logo
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {logos.map((logo) => (
              <div
                key={logo._id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Image Preview */}
                <div className="aspect-square bg-gray-50 p-6 flex items-center justify-center">
                  <img
                    src={logo.secure_url}
                    alt={logo.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.src = '/placeholder-logo.svg';
                    }}
                  />
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-1 truncate">
                    {logo.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {logo.category}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      {logo.original_format?.toUpperCase()}
                    </span>
                    {logo.is_svg && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        SVG
                      </span>
                    )}
                    <span>{logo.width}×{logo.height}</span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <div className="flex items-center">
                      <Download className="w-3 h-3 mr-1" />
                      {logo.downloadCount || 0}
                    </div>
                    <div className="flex items-center">
                      <ImageIcon className="w-3 h-3 mr-1" />
                      {logo.transformationCount || 0}
                    </div>
                  </div>

                  {/* Tags */}
                  {logo.tags && logo.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {logo.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleTransform(logo)}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Transform
                    </button>
                    <button
                      onClick={() => handleDelete(logo._id)}
                      className="px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Panel Modal */}
      {showUploadPanel && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowUploadPanel(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <AdminUploadPanel
              onUploadSuccess={handleUploadSuccess}
              onClose={() => setShowUploadPanel(false)}
            />
          </div>
        </div>
      )}

      {/* Transform Panel Modal */}
      {showTransformPanel && selectedLogo && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowTransformPanel(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <LogoTransformPanel
              logo={selectedLogo}
              onClose={() => setShowTransformPanel(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
